function setPositionFromClientX(parent, clientX) {
  const rect = parent.getBoundingClientRect();
  const x = clientX - rect.left;
  const width = rect.width;
  const clampedX = Math.max(0, Math.min(x, width));
  const xPercent = Math.round((clampedX / width) * 100);
  parent.style.setProperty('--position', `${xPercent}%`);
  return xPercent;
}

function createEmitter() {
  const map = new Map();
  const on = (type, fn) => {
    if (!map.has(type)) map.set(type, new Set());
    map.get(type).add(fn);
    return () => off(type, fn);
  };
  const off = (type, fn) => {
    const set = map.get(type);
    if (!set) return;
    set.delete(fn);
    if (set.size === 0) map.delete(type);
  };
  const emit = (type, detail) => {
    const set = map.get(type);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(detail);
      } catch (_) {}
    }
  };
  return { on, off, emit };
}

function setDraggingClass(el, on) {
  el.classList.toggle('is-dragging', !!on);
}

function initWithPointerEvents(parent, emit) {
  parent.style.touchAction = 'none';

  let dragging = false;
  let activePointerId = null;
  let moved = false;

  const onDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;

    dragging = true;
    moved = false;
    activePointerId = e.pointerId;

    try {
      parent.setPointerCapture(e.pointerId);
    } catch (_) {}
    const pos = setPositionFromClientX(parent, e.clientX);

    setDraggingClass(parent, true);
    emit('dragstart', { event: e, position: pos });

    e.preventDefault();
    e.stopPropagation();
  };

  const onMove = (e) => {
    if (!dragging || e.pointerId !== activePointerId) return;
    const pos = setPositionFromClientX(parent, e.clientX);
    moved = true;
    emit('drag', { event: e, position: pos });

    e.preventDefault();
    e.stopPropagation();
  };

  const onEnd = (e) => {
    if (
      !dragging ||
      (e.pointerId !== undefined && e.pointerId !== activePointerId)
    )
      return;
    dragging = false;

    try {
      parent.releasePointerCapture(activePointerId);
    } catch (_) {}
    setDraggingClass(parent, false);
    emit('dragend', { event: e });

    e.preventDefault();
    e.stopPropagation();
  };

  const onClick = (e) => {
    if (moved) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const pos = setPositionFromClientX(parent, e.clientX);
    emit('click', { event: e, position: pos });
    e.stopPropagation();
  };

  parent.addEventListener('pointerdown', onDown);
  parent.addEventListener('pointermove', onMove);
  parent.addEventListener('pointerup', onEnd);
  parent.addEventListener('pointercancel', onEnd);
  parent.addEventListener('lostpointercapture', onEnd);
  parent.addEventListener('click', onClick);

  return () => {
    parent.removeEventListener('pointerdown', onDown);
    parent.removeEventListener('pointermove', onMove);
    parent.removeEventListener('pointerup', onEnd);
    parent.removeEventListener('pointercancel', onEnd);
    parent.removeEventListener('lostpointercapture', onEnd);
    parent.removeEventListener('click', onClick);
    setDraggingClass(parent, false);
  };
}

function initWithTouchMouseFallback(parent, emit) {
  parent.style.touchAction = 'none';

  let dragging = false;
  let moved = false;

  const getTouchClientX = (e) =>
    e.touches && e.touches.length ? e.touches[0].clientX : null;

  const onTouchStart = (e) => {
    const x = getTouchClientX(e);
    if (x != null) {
      const pos = setPositionFromClientX(parent, x);
      emit('dragstart', { event: e, position: pos });
    }
    dragging = true;
    moved = false;

    setDraggingClass(parent, true);

    e.preventDefault();
    e.stopPropagation();
  };

  const onTouchMove = (e) => {
    if (!dragging) return;
    const x = getTouchClientX(e);
    if (x != null) {
      const pos = setPositionFromClientX(parent, x);
      moved = true;
      emit('drag', { event: e, position: pos });
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const onTouchEnd = (e) => {
    if (!dragging) return;
    dragging = false;

    setDraggingClass(parent, false);
    emit('dragend', { event: e });

    e.preventDefault();
    e.stopPropagation();
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    dragging = true;
    moved = false;
    const pos = setPositionFromClientX(parent, e.clientX);

    setDraggingClass(parent, true);
    emit('dragstart', { event: e, position: pos });

    e.preventDefault();
    e.stopPropagation();
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    const pos = setPositionFromClientX(parent, e.clientX);
    moved = true;
    emit('drag', { event: e, position: pos });
    e.preventDefault();
    e.stopPropagation();
  };

  const onMouseUp = (e) => {
    if (!dragging) return;
    dragging = false;

    setDraggingClass(parent, false);
    emit('dragend', { event: e });

    e.preventDefault();
    e.stopPropagation();
  };

  const onClick = (e) => {
    if (moved) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const pos = setPositionFromClientX(parent, e.clientX);
    emit('click', { event: e, position: pos });
    e.stopPropagation();
  };

  parent.addEventListener('touchstart', onTouchStart, { passive: false });
  parent.addEventListener('touchmove', onTouchMove, { passive: false });
  parent.addEventListener('touchend', onTouchEnd, { passive: false });
  parent.addEventListener('touchcancel', onTouchEnd, { passive: false });

  parent.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);

  parent.addEventListener('click', onClick);

  return () => {
    parent.removeEventListener('touchstart', onTouchStart);
    parent.removeEventListener('touchmove', onTouchMove);
    parent.removeEventListener('touchend', onTouchEnd);
    parent.removeEventListener('touchcancel', onTouchEnd);

    parent.removeEventListener('mousedown', onMouseDown);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);

    parent.removeEventListener('click', onClick);
    setDraggingClass(parent, false);
  };
}

export function sideBySideCompare(element) {
  if (!element) throw new Error('sideBySideCompare: element is required');

  const { on, off, emit } = createEmitter();
  const teardown = window.PointerEvent
    ? initWithPointerEvents(element, emit)
    : initWithTouchMouseFallback(element, emit);

  return {
    on,
    off,
    destroy() {
      teardown();
      setDraggingClass(element, false);
    },
    set(xPercent) {
      const width = element.getBoundingClientRect().width;
      const x = Math.max(0, Math.min(xPercent, 100));
      const px = (x / 100) * width;
      setPositionFromClientX(
        element,
        element.getBoundingClientRect().left + px,
      );
    },
    get() {
      const v = getComputedStyle(element).getPropertyValue('--position').trim();
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    },
  };
}
